<template>
  <v-card flat elevation="3" class="cola-card">

    <v-alert v-if="empresaError" type="warning" dense text class="ma-3">
      {{ empresaError }}
    </v-alert>

    <!-- Barra de acciones. Ocupa lugar siempre para que la tabla no salte
         cuando aparece la selección. -->
    <div class="toolbar-slot">
      <v-slide-y-transition>
        <v-toolbar v-if="totalSeleccionadas > 0" dense flat class="actions-toolbar">
          <v-icon color="primary" class="mr-2">mdi-check-circle-outline</v-icon>
          <span class="seleccion-texto">
            <strong>{{ totalSeleccionadas }}</strong>
            de {{ resumen.totalAptas }} seleccionadas
          </span>

          <v-chip v-if="resumen.excedeLimite" small color="warning" class="ml-3">
            Se procesarán las primeras {{ resumen.limite }}
          </v-chip>

          <v-spacer />

          <v-btn text small class="mr-2" @click="limpiarSeleccion">
            <v-icon small left>mdi-close</v-icon>
            Limpiar
          </v-btn>
          <v-btn color="primary" small depressed :disabled="!puedeEmitir" @click="$emit('emitir', payloadSeleccion)">
            <v-icon small left>mdi-file-document-multiple</v-icon>
            Facturar seleccionadas
          </v-btn>
        </v-toolbar>
      </v-slide-y-transition>
    </div>

    <v-card-title class="pt-0 pb-2">
      <v-text-field
        v-model="search"
        label="Buscar por número de orden o cliente"
        prepend-inner-icon="mdi-magnify"
        dense outlined hide-details clearable
        class="buscador"
        @input="buscarConDebounce"
      />
      <v-spacer />
      <v-checkbox
        v-model="enviarEmail"
        label="Enviar por email al emitir"
        dense hide-details class="mt-0 pt-0 ml-4"
        @change="$emit('enviar-email', enviarEmail)"
      />
    </v-card-title>

    <v-data-table
      v-model="seleccionPagina"
      show-select
      item-key="ordenVentaId"
      :headers="headers"
      :items="items"
      :loading="loading"
      :server-items-length="totalItems"
      :options.sync="options"
      :item-class="claseFila"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
      loading-text="Cargando órdenes…"
      no-data-text="No hay órdenes de venta pendientes de facturar con estos filtros."
      class="cola-tabla"
      @item-selected="onItemSeleccionado"
      @toggle-select-all="onToggleTodos"
    >
      <template v-slot:item.data-table-select="{ item, isSelected, select }">
        <v-simple-checkbox
          :value="isSelected"
          :disabled="!item.apto"
          :ripple="false"
          @input="select($event)"
        />
      </template>

      <template v-slot:item.numeroOrdenVenta="{ item }">
        <span class="font-weight-medium">#{{ item.numeroOrdenVenta }}</span>
      </template>

      <template v-slot:item.clienteRazonSocial="{ item }">
        <div>{{ item.clienteRazonSocial || "Sin cliente" }}</div>
        <div v-if="item.clienteCondicionIVA" class="caption grey--text">
          {{ etiquetaCondicion(item.clienteCondicionIVA) }}
        </div>
      </template>

      <template v-slot:item.tipoComprobante="{ item }">
        <v-chip v-if="item.tipoComprobante" x-small outlined>
          {{ etiquetaTipo(item.tipoComprobante) }}
        </v-chip>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.precioTotal="{ item }">
        <span class="font-weight-medium">{{ moneda(item.precioTotal) }}</span>
      </template>

      <template v-slot:item.fecha="{ item }">
        {{ fechaCorta(item.fecha) }}
      </template>

      <template v-slot:item.apto="{ item }">
        <v-tooltip bottom :disabled="item.apto">
          <template v-slot:activator="{ on }">
            <v-chip
              x-small
              :color="item.apto ? 'success' : 'grey lighten-1'"
              :text-color="item.apto ? 'white' : 'grey darken-2'"
              v-on="on"
            >
              {{ item.apto ? "Lista" : "Bloqueada" }}
            </v-chip>
          </template>
          <span>{{ item.motivo }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import FacturacionProvider from "../../providers/FacturacionProvider";

const MODO_TODOS = "TODOS_FILTRADOS";
const MODO_SELECCIONADOS = "SOLO_SELECCIONADOS";

export default {
  name: "FacturacionMasivaCola",

  props: {
    filtros: { type: Object, default: () => ({}) },
    /** Cuando viene con IDs, la cola arranca acotada a esas órdenes. */
    initialOrdenVentaIds: { type: Array, default: null },
  },

  data() {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      search: null,
      debounce: null,
      enviarEmail: false,
      empresaError: null,
      options: { page: 1, itemsPerPage: 25, sortBy: [], sortDesc: [] },
      resumen: { totalAptas: 0, totalBloqueadas: 0, excedeLimite: false, limite: 500 },

      /**
       * La selección se guarda por EXCLUSIÓN, no acumulando filas.
       *
       * Con cientos de órdenes repartidas en muchas páginas, un array de filas
       * seleccionadas se perdería al paginar. Acá el estado por defecto es
       * "todo lo que matchea el filtro", y solo se recuerda lo que el usuario
       * destildó. Es también lo que el backend espera recibir.
       */
      modoSeleccion: MODO_TODOS,
      excluidos: [],
      incluidos: [],

      seleccionPagina: [],
    };
  },

  computed: {
    headers() {
      return [
        { text: "Orden", value: "numeroOrdenVenta", width: 90 },
        // No se puede ordenar por razón social: vive en Cliente, no en OrdenVenta.
        { text: "Cliente", value: "clienteRazonSocial", sortable: false },
        { text: "Comprobante", value: "tipoComprobante", width: 130, sortable: false },
        { text: "Vendedor", value: "vendedorNombre", sortable: false },
        { text: "Total", value: "precioTotal", align: "end", width: 120 },
        { text: "Fecha", value: "fecha", width: 110, sortable: false },
        { text: "Estado", value: "apto", align: "center", width: 110, sortable: false },
      ];
    },

    totalSeleccionadas() {
      if (this.modoSeleccion === MODO_SELECCIONADOS) return this.incluidos.length;
      const tope = Math.min(this.resumen.totalAptas, this.resumen.limite);
      return Math.max(tope - this.excluidos.length, 0);
    },

    puedeEmitir() {
      return this.totalSeleccionadas > 0 && !this.empresaError;
    },

    payloadSeleccion() {
      return {
        modoSeleccion: this.modoSeleccion,
        ordenVentaIds: this.modoSeleccion === MODO_SELECCIONADOS ? this.incluidos : null,
        excluirOrdenVentaIds: this.modoSeleccion === MODO_TODOS ? this.excluidos : null,
        enviarEmail: this.enviarEmail,
        totalEstimado: this.totalSeleccionadas,
      };
    },

    /** Los filtros que van al backend, incluidos los IDs iniciales del diálogo. */
    filtrosEfectivos() {
      return { ...this.filtros, enviarEmail: this.enviarEmail };
    },
  },

  watch: {
    filtros: {
      deep: true,
      handler() {
        // Cambiar el filtro cambia el universo: la selección previa deja de
        // tener sentido.
        this.limpiarSeleccion();
        this.options.page = 1;
        this.recargar();
      },
    },
    options: {
      deep: true,
      handler() {
        this.fetch();
      },
    },
  },

  created() {
    if (this.initialOrdenVentaIds && this.initialOrdenVentaIds.length > 0) {
      this.modoSeleccion = MODO_SELECCIONADOS;
      this.incluidos = [...this.initialOrdenVentaIds];
    }
  },

  mounted() {
    this.recargar();
  },

  beforeDestroy() {
    if (this.debounce) clearTimeout(this.debounce);
  },

  methods: {
    async recargar() {
      await Promise.all([this.fetch(), this.fetchResumen()]);
    },

    async fetch() {
      this.loading = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;
        const r = await FacturacionProvider.facturacionMasivaCola(
          page,
          itemsPerPage,
          this.search,
          sortBy && sortBy.length ? sortBy[0] : null,
          Boolean(sortDesc && sortDesc[0]),
          this.filtrosParaConsulta()
        );
        const data = r.data.facturacionMasivaCola;
        this.items = data.items;
        this.totalItems = data.totalItems;
        this.empresaError = data.empresaValida ? null : data.empresaError;
        this.sincronizarSeleccionDePagina();
      } catch (e) {
        this.$emit("error", e.message);
      } finally {
        this.loading = false;
      }
    },

    async fetchResumen() {
      try {
        const r = await FacturacionProvider.facturacionMasivaResumen(this.filtrosParaConsulta());
        this.resumen = r.data.facturacionMasivaResumen;
      } catch (e) {
        // El resumen es informativo: si falla, la tabla sigue usable.
      }
    },

    filtrosParaConsulta() {
      const base = { ...this.filtros };
      delete base.enviarEmail;
      // En modo diálogo la cola se acota a las órdenes que vinieron de Ventas.
      if (this.initialOrdenVentaIds && this.initialOrdenVentaIds.length > 0) {
        base.ordenVentaIds = this.initialOrdenVentaIds;
      }
      return base;
    },

    /**
     * Pre-tilda todo lo apto que no esté excluido. Es lo que hace que el usuario
     * abra la pantalla con la selección hecha y solo tenga que destildar.
     */
    sincronizarSeleccionDePagina() {
      if (this.modoSeleccion === MODO_SELECCIONADOS) {
        this.seleccionPagina = this.items.filter(
          (i) => i.apto && this.incluidos.includes(i.ordenVentaId)
        );
        return;
      }
      this.seleccionPagina = this.items.filter(
        (i) => i.apto && !this.excluidos.includes(i.ordenVentaId)
      );
    },

    onItemSeleccionado({ item, value }) {
      if (this.modoSeleccion === MODO_SELECCIONADOS) {
        if (value) {
          if (!this.incluidos.includes(item.ordenVentaId)) this.incluidos.push(item.ordenVentaId);
        } else {
          this.incluidos = this.incluidos.filter((id) => id !== item.ordenVentaId);
        }
        return;
      }

      if (value) {
        this.excluidos = this.excluidos.filter((id) => id !== item.ordenVentaId);
      } else if (!this.excluidos.includes(item.ordenVentaId)) {
        this.excluidos.push(item.ordenVentaId);
      }
    },

    onToggleTodos({ items, value }) {
      const idsAptos = items.filter((i) => i.apto).map((i) => i.ordenVentaId);

      if (this.modoSeleccion === MODO_SELECCIONADOS) {
        this.incluidos = value
          ? [...new Set([...this.incluidos, ...idsAptos])]
          : this.incluidos.filter((id) => !idsAptos.includes(id));
        return;
      }

      if (value) {
        this.excluidos = this.excluidos.filter((id) => !idsAptos.includes(id));
      } else {
        this.excluidos = [...new Set([...this.excluidos, ...idsAptos])];
      }
    },

    limpiarSeleccion() {
      this.modoSeleccion = MODO_TODOS;
      this.excluidos = [];
      this.incluidos = [];
      this.sincronizarSeleccionDePagina();
    },

    buscarConDebounce() {
      if (this.debounce) clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.options.page = 1;
        this.recargar();
      }, 400);
    },

    claseFila(item) {
      return item.apto ? "" : "fila-bloqueada";
    },

    moneda(valor) {
      if (valor == null) return "—";
      return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      }).format(valor);
    },

    fechaCorta(valor) {
      if (!valor) return "—";
      const d = new Date(Number.isNaN(Number(valor)) ? valor : Number(valor));
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("es-AR");
    },

    etiquetaTipo(tipo) {
      return (tipo || "").replace("FACTURA_", "Factura ");
    },

    etiquetaCondicion(cond) {
      const mapa = {
        CONSUMIDOR_FINAL: "Consumidor final",
        RESPONSABLE_INSCRIPTO: "Responsable inscripto",
        MONOTRIBUTISTA: "Monotributista",
        EXENTO: "Exento",
        NO_CATEGORIZADO: "No categorizado",
      };
      return mapa[cond] || cond;
    },
  },
};
</script>

<style scoped>
.cola-card {
  border-radius: 12px !important;
}

/* Altura reservada: evita que la tabla salte al aparecer la barra. */
.toolbar-slot {
  min-height: 48px;
}

.actions-toolbar {
  background: #e8eaf6 !important;
  border-radius: 12px 12px 0 0;
}

.seleccion-texto {
  font-size: 0.9rem;
  color: #2c3e50;
}

.buscador {
  max-width: 420px;
}

.cola-tabla >>> .fila-bloqueada {
  background-color: #fafafa;
  color: #9e9e9e;
}
</style>
